
import React, { useState, useEffect } from 'react';
import { useAppointmentHistory } from './hooks/useAppointmentHistory';
import { Loader2, Search, RefreshCcw } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { RefundRequestModal } from '@/components/dashboard/RefundRequestModal';
import { EmptyAppointmentHistory } from './appointments/EmptyAppointmentHistory';
import { PendingRefundRequests } from './appointments/PendingRefundRequests';
import { AppointmentHistoryTable } from './appointments/AppointmentHistoryTable';
import { AppointmentHistoryPagination } from './appointments/AppointmentHistoryPagination';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

interface AppointmentHistoryProps {
  unreadByBooking?: Record<string, number>;
}

const AppointmentHistory: React.FC<AppointmentHistoryProps> = ({ unreadByBooking = {} }) => {
  const { t } = useTranslation('dashboard');
  const { user } = useAuth();
  const {
    appointments,
    isLoading,
    isDeleting,
    appointmentToDelete,
    selectedAppointmentForRefund,
    setAppointmentToDelete,
    setSelectedAppointmentForRefund,
    fetchAppointments,
    refreshAppointments,
    handleDeleteAppointment
  } = useAppointmentHistory();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [userTimezone, setUserTimezone] = useState<string>('America/New_York');

  // Fetch user timezone
  useEffect(() => {
    const fetchUserTimezone = async () => {
      if (!user?.id) return;
      
      try {
        const { data, error } = await supabase
          .from('clients')
          .select('timezone')
          .eq('id', user.id)
          .single();
        
        if (!error && data?.timezone) {
          setUserTimezone(data.timezone);
        }
      } catch (error) {
        console.error('Error fetching user timezone:', error);
      }
    };
    
    fetchUserTimezone();
  }, [user?.id]);

  useEffect(() => {
    console.log('All appointments in history:', appointments);
  }, [appointments]);

  const getPractitionerName = (practitioner: { first_name: string | null; last_name: string | null }) => {
    if (!practitioner) return 'Unknown';
    return `${practitioner.first_name || ''} ${practitioner.last_name || ''}`.trim() || 'Unknown';
  };

  const filteredAppointments = appointments.filter(appointment => 
    appointment.service?.service_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    getPractitionerName(appointment.practitioner).toLowerCase().includes(searchTerm.toLowerCase()) ||
    appointment.status?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (appointment.payment_status && appointment.payment_status.toLowerCase().includes(searchTerm.toLowerCase()))
  );
  
  useEffect(() => {
    console.log('Filtered appointments:', filteredAppointments);
  }, [filteredAppointments]);
  
  const totalPages = Math.ceil(filteredAppointments.length / 10);
  const indexOfLastAppointment = currentPage * 10;
  const indexOfFirstAppointment = indexOfLastAppointment - 10;
  const currentAppointments = filteredAppointments.slice(indexOfFirstAppointment, indexOfLastAppointment);

  useEffect(() => {
    console.log('Current appointments to display:', currentAppointments);
  }, [currentAppointments]);
  
  // Reset to first page when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const handleRefresh = () => {
    refreshAppointments();
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[300px]">
        <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col gap-4 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <h2 className="text-xl sm:text-2xl font-medium truncate">History</h2>
            <Button 
              variant="outline" 
              size="icon" 
              onClick={handleRefresh} 
              title={t('refreshAppointments')}
              aria-label={t('refreshAppointments')}
              className="h-8 w-8 shrink-0"
            >
              <RefreshCcw className="h-4 w-4" />
            </Button>
          </div>
          
          <div className="relative w-full sm:w-auto sm:min-w-[250px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t('searchAppointments')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-full"
            />
          </div>
        </div>
      </div>
      
      {appointments.length > 0 ? (
        <>
          <div className="mb-4 text-sm text-gray-500">
            {t('showingResults', { 
              current: currentAppointments.length, 
              filtered: filteredAppointments.length 
            })} {t('totalAppointments', { total: appointments.length })}
          </div>
          <AppointmentHistoryTable
            appointments={currentAppointments}
            onRefundRequest={(id) => setSelectedAppointmentForRefund(id)}
            onDeleteAppointment={handleDeleteAppointment}
            unreadByBooking={unreadByBooking}
            userTimezone={userTimezone}
          />
          
          <PendingRefundRequests 
            appointments={currentAppointments} 
            onStatusUpdate={fetchAppointments} 
          />
          
          <AppointmentHistoryPagination
            currentPage={currentPage}
            totalPages={totalPages}
            setCurrentPage={setCurrentPage}
          />
        </>
      ) : (
        <EmptyAppointmentHistory />
      )}

      {selectedAppointmentForRefund && (
        <RefundRequestModal
          isOpen={!!selectedAppointmentForRefund}
          onClose={() => setSelectedAppointmentForRefund(null)}
          bookingId={selectedAppointmentForRefund}
        />
      )}
    </div>
  );
};

export default AppointmentHistory;
